/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { formattedDate, getLoggedUserGPID } from 'utils';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Link,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContentText,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import axios from 'axios';
import ErrorFallback from 'components/error-fallback';
import Grid from '@mui/material/Grid';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { endPoints } from 'pages/dlops-dashboard/api/endpoint';
import { PEPVIGIL } from 'pages/dlops-dashboard/utils/constant';
import { commonStyles as styles } from 'assets/style';
import { Item, StyledTableHeader, StyledTableCell, style, ProgressIcon } from '../style';
import AccessToken from '../components/accessToken/accessToken';
import modeltrain from '../../../assets/images/dlopsImages/model_training_FILL0_wght400_GRAD0_opsz24.svg';
import TeamMemberDrawer from '../modelOnBoarding/createTeamMembers';
import breadcrumbIcon from '../../../assets/images/dlopsImages/arrow_forward.svg';

const DlopsDashboard = () => {
    const { getAccessToken, handleTokenError } = AccessToken();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [uniqueModelname, setUniqueModelName] = useState('');
    const [isLoading, setLoading] = useState(true);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const history = useHistory();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const goToViewModelVersion = (modelName) => {
        history.push('/pepvigil-model-versions');
        localStorage.setItem('modelName', modelName);
    };

    const columns = [
        { name: 'Model name' },
        { name: 'Version' },
        { name: 'Type' },
        { name: 'Created Date' },
        { name: 'Last Updated Date' },
        { name: 'Model versions' },
    ];

    const fetchModelList = async (accessToken, gpid) => {
        try {
            return await axios.get(endPoints.registeredModel, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    gpid,
                },
            });
        } catch (error) {
            return handleTokenError(error, endPoints.registeredModel, 'get', null);
        }
    };
    const getRegisteredDlopsModelVersion = async (accessTokenPromise, gpid) => {
        setLoading(true);
        try {
            const accessToken = await accessTokenPromise;
            const response = await axios.get(endPoints.getAllRegisteredModelVersionFromDlops, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/json',
                    gpid,
                },
            });
            setLoading(false);
            return response?.data;
        } catch (error) {
            setLoading(false);
            return error;
        }
    };
    const getProjectModelsList = async () => {
        try {
            const gpid = getLoggedUserGPID();
            const accessToken = await getAccessToken();
            setLoading(true);
            const modelVersionStorage = localStorage.getItem('modelVersionsCheck');
            let response;
            let registeredModelResponse;
            let onBoardModelResponse;
            if (modelVersionStorage === 'false') {
                response = await fetchModelList(accessToken, gpid);
                registeredModelResponse = response?.data?.registered_models;
            } else {
                response = await getRegisteredDlopsModelVersion(accessToken, gpid);
                onBoardModelResponse = response?.registered_models;
            }
            const filterResponse = modelVersionStorage === 'false' ? registeredModelResponse : onBoardModelResponse;
            setLoading(false);
            const getProjectNameFromStorage = localStorage.getItem('project_name');
            const filteredData = filterResponse
                .flatMap((model) =>
                    model.latest_versions.filter((version) => version?.tags?.some((tag) => tag.value.toLowerCase() === getProjectNameFromStorage.toLowerCase()))
                )
                .filter((removeStaging) => removeStaging.current_stage !== 'Staging');
            const segregatedData = {};

            filteredData.forEach((item) => {
                const { name } = item;
                if (!segregatedData[name]) {
                    segregatedData[name] = [];
                }
                segregatedData[name].push(item);
            });

            const result = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const key in segregatedData) {
                if (Object.prototype.hasOwnProperty.call(segregatedData, key)) {
                    const dataArray = segregatedData[key];
                    if (dataArray.length === 1) {
                        result.push(dataArray[0]);
                    } else {
                        let preferredObject = null;

                        const sortAndPickPreferred = (objects, stage) => {
                            const stageObjects = objects.filter((obj) => obj.current_stage === stage);
                            if (stageObjects.length > 0) {
                                stageObjects.sort((a, b) => b.version - a.version);
                                [preferredObject] = stageObjects;
                            }
                        };

                        sortAndPickPreferred(dataArray, 'Production');
                        if (!preferredObject) {
                            sortAndPickPreferred(dataArray, 'Archived');
                        }
                        if (!preferredObject) {
                            sortAndPickPreferred(dataArray, 'None');
                        }
                        if (preferredObject) {
                            result.push(preferredObject);
                        }
                    }
                }
            }
            setUniqueModelName(result);

            const latestVersions = {};

            filteredData.forEach((entry) => {
                const { name } = entry;
                const version = parseInt(entry.version, 10);

                if (!(name in latestVersions) || version > parseInt(latestVersions[name].version, 10)) {
                    latestVersions[name] = entry;
                }
            });

            return Object.values(latestVersions);
        } catch (error) {
            setLoading(false);
            return error;
        }
    };
    const handleNavigation = () => {
        history.push('/pepvigil-projects');
    };
    const handleAddNewModel = () => {
        localStorage.removeItem('addNewModel');
        localStorage.removeItem('addNewtags');
        history.push('/pepvigil-add-new-model');
    };
    useEffect(() => {
        getProjectModelsList();
    }, []);

    const handleAddTeamMember = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleYesClick = () => {
        setDrawerOpen(true);
        setDialogOpen(false);
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };
    const handleDrawerSubmit = () => {
        history.push('/pepvigil-registered-models');
    };
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Helmet>
                <title>{PEPVIGIL} Dashboard</title>
            </Helmet>
            <Box>
                <Grid container>
                    <Grid item xs={12}>
                        <Typography variant="h6">
                            <Item sx={style.subHeader}>
                                <div style={{ marginTop: '8px', fontSize: '14px', textAlign: 'left' }} className="contentContainer">
                                    <span>{PEPVIGIL}</span>
                                    <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                    <span aria-hidden="true" style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleNavigation()}>
                                        Projects
                                    </span>
                                    <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                    <span style={{ fontWeight: 'bold', color: '#005CBC' }}>Models</span>
                                </div>
                            </Item>
                        </Typography>
                    </Grid>
                </Grid>
                <Box className="contentContainer">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Button
                            variant="contained"
                            disableElevation
                            disableRipple
                            //  color="primary"
                            style={{ ...style.projectButton, marginLeft: '0px' }}
                        >
                            {localStorage.getItem('project_name')}
                        </Button>

                        {/* Grouped buttons */}
                        <Box sx={{ display: 'flex', gap: '10px' }}>
                            <Button disableElevation disableRipple variant="contained" sx={styles.primaryMediumButton} onClick={handleAddTeamMember}>
                                Add Team Member
                            </Button>
                        </Box>
                    </Box>
                    <Dialog
                        open={isDialogOpen}
                        onClose={handleDialogClose}
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '480px', // Set desired width
                                height: '250px', // Set desired height
                                display: 'flex', // Use flex for alignment
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center', // Center content horizontally
                                padding: '20px', // Add padding for spacing
                                border: '10px solid #D7E5F8',
                            },
                        }}
                    >
                        <DialogTitle sx={styles.modalTitle}>Access to the Team Members </DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={styles.modalContent}>
                                Would you like to provide access to the team members for the project?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button disableElevation disableRipple variant="contained" onClick={handleYesClick} sx={styles.modalSaveButton}>
                                Yes
                            </Button>
                            <Button disableElevation disableRipple variant="outlined" onClick={handleDialogClose} sx={styles.modalCancelButton}>
                                No
                            </Button>
                        </DialogActions>
                    </Dialog>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100vh' }}>
                            <CircularProgress color="inherit" size={16} sx={ProgressIcon.circularProgressColor} />
                            <Typography variant="body2" sx={ProgressIcon.circularProgressColor} style={{ marginLeft: '10px' }}>
                                Loading
                            </Typography>
                        </Box>
                    ) : (
                        <div style={style.dataTable}>
                            <div
                                style={{
                                    ...style.allProjectDiv,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {/* Left Section: Project Models Title */}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <img src={modeltrain} alt="Model Train" style={style.stickyNote} />
                                    <h5 style={{ ...style.projectText, marginTop: '0px !important' }}>Project Models ({uniqueModelname.length})</h5>
                                </div>

                                {/* Right Section: Add a New Model Button */}
                                <Button
                                    variant="contained"
                                    // color="primary"
                                    disableElevation
                                    disableRipple
                                    sx={{ ...styles.modalSaveButton, marginRight: '20px', height: '36px', boxShadow: 'none' }}
                                    onClick={handleAddNewModel}
                                >
                                    Add a New Model
                                </Button>
                            </div>
                            <TeamMemberDrawer open={isDrawerOpen} onClose={handleDrawerClose} onSubmit={handleDrawerSubmit} sx={{ width: '90%' }} />
                            <TableContainer component={Paper} sx={style.tablePaper}>
                                {uniqueModelname.length > 0 ? (
                                    <Table>
                                        <TableHead sx={{ backgroundColor: (theme) => theme.palette.cyan['90'] }}>
                                            <TableRow>
                                                {columns.map((column, index) => (
                                                    <StyledTableHeader key={index} maxWidth={column.maxWidth}>
                                                        {column.name}
                                                    </StyledTableHeader>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {uniqueModelname.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index1) => (
                                                <TableRow key={index1}>
                                                    <StyledTableCell sx={{ color: '#005CBC', fontWeight: 'normal' }}>{row?.name}</StyledTableCell>
                                                    <StyledTableCell sx={{ color: '#3A3A3A' }}>{row?.version}</StyledTableCell>
                                                    <StyledTableCell sx={{ color: '#3A3A3A' }}>
                                                        {row?.tags.find((tag) => tag.key === 'model_type')?.value || 'undefined'}
                                                    </StyledTableCell>
                                                    <StyledTableCell sx={{ color: '#3A3A3A' }}>{formattedDate(row?.creation_timestamp)}</StyledTableCell>
                                                    <StyledTableCell sx={{ color: '#3A3A3A' }}>
                                                        {formattedDate(row?.last_updated_timestamp || row?.creation_timestamp)}
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Link
                                                            to="/pepvigil-model-versions"
                                                            onClick={() => goToViewModelVersion(row.name)}
                                                            style={{
                                                                color: '#005CBC',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    color: '#004A96',
                                                                },
                                                            }}
                                                        >
                                                            View model versions
                                                        </Link>
                                                    </StyledTableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Typography variant="body1" style={{ textAlign: 'center', marginTop: '20px', marginLeft: '0px', marginBottom: '20px' }}>
                                        No Registered Model to display
                                    </Typography>
                                )}

                                {uniqueModelname.length > 0 && (
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 100]}
                                        component="div"
                                        count={uniqueModelname.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                )}
                            </TableContainer>
                        </div>
                    )}
                </Box>
            </Box>
        </ErrorBoundary>
    );
};

export default DlopsDashboard;
